import { useMutation, useQuery } from "@tanstack/react-query";
import { putScoreInput, scoreCardDelete } from "../../@apis/v1/score.service";
import AppBar from "../../components/atoms/appbar";
import { vw } from "../../lib/utils/responsiveUtils";
import { useGetRoundList, useGetScoreDetail } from "../../@apis/hooks/useScore";
import { useGetMain } from "../../@apis/hooks/useMain";
import { useNavigate, useParams } from "react-router-dom";
import * as React from "react";
import type { ScoreCard } from "../../@apis/dto/response/score.response";
import {
  courseLabelOf,
  courseOrderOf,
  holeAt,
  inputHoleNoAfter,
  needsUserPar,
  nextInputHoleNo,
  resumeHoleNo,
} from "../../lib/utils/score.utils";
import { staticURL } from "../../lib/utils/imageUtils";
import { formatKoreanDate } from "../../lib/utils/dateUtils";
import AnimatedCoverTabs from "../../components/atoms/tab/AnimatedConverTab";

import ScorePersonalDrawer from "../../components/atoms/drawer/ScorePersonalDrawer";
import ParPersonalDrawer from "../../components/atoms/drawer/ParPersonalDrawer";
import { useModalStore } from "../../lib/store/useModalStore";
import { getQueryClient } from "../../@apis/queryClient";
import { alertRequestFailure } from "../../@apis/axios.config";
import { useOvertimeSummaryList } from "../../@apis/hooks/useTournament";
import ScorecardTab from "./components/ScorecardTab";
import RankingTab from "./components/RankingTab";

export type DrawerCore = {
  holeNo: number;
  par: number;
  isEdit: boolean;
  score: number | null;
};

export default function ScoreDetailPage() {
  const navigate = useNavigate();
  const { openModal } = useModalStore();

  const { id } = useParams<{ id: string }>();

  // MULTI_ROUND 판별: /v1/main 응답에서 현재 scoreCardId와 매칭된 항목의 tournamentType 확인
  const { data: mainData } = useQuery({ ...useGetMain() });
  const matchedTournamentInfo = React.useMemo(
    () =>
      mainData?.data?.tournamentInfoList?.find(
        (t) => t.scoreCardId === Number(id),
      ),
    [mainData, id],
  );
  const isMultiRound = matchedTournamentInfo?.tournamentType === "MULTI_ROUND";

  // 라운드 목록 (MULTI_ROUND일 때만)
  const { data: roundListData } = useQuery({
    ...useGetRoundList(
      isMultiRound ? (matchedTournamentInfo?.tournamentId ?? null) : null,
    ),
  });
  const roundList = roundListData?.data?.roundList ?? [];

  // URL의 scoreCardId와 매칭되는 라운드
  const currentRound = roundList.find((r) => r.scoreCardId === Number(id));
  const currentRoundId = currentRound?.id ?? null;

  const effectiveRoundId = isMultiRound ? currentRoundId : null;
  const scoreDetailQuery = useGetScoreDetail(Number(id), effectiveRoundId);
  const {
    data: scoreDetailData,
    isSuccess,
    refetch: refetchScoreDetail,
  } = useQuery({
    ...useGetScoreDetail(Number(id), effectiveRoundId),
    enabled: !isMultiRound || currentRound != null,
    refetchInterval: 30 * 1000,
  });

  const {
    scoreCardList = [],
    tournamentId,
    thumbnail,
    tournamentName,
    teamName,
    tournamentType,
  } = scoreDetailData?.data || {};

  const roundId = scoreCardList[0]?.roundId ?? null;

  // 오버타임 목록 (roundId가 있을 때만 호출)
  const { data: overtimeListData, refetch: refetchOvertime } = useQuery({
    ...useOvertimeSummaryList(tournamentId, roundId ?? 0),
    enabled: roundId != null,
  });

  const [isSpinning, setIsSpinning] = React.useState(false);

  const handleRefresh = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    refetchScoreDetail();
    if (roundId != null) refetchOvertime();
    // 순위 탭 콘텐츠(순위 목록 + 펼친 스코어 카드) 리패치
    const queryClient = getQueryClient();
    [
      "score-order-list",
      "round-score-order-list",
      "score-card-view",
      "user-score-cards",
    ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    setTimeout(() => setIsSpinning(false), 1000);
  };
  const currentUserId = overtimeListData?.data?.userId;
  const currentOvertimes = overtimeListData?.data?.rounds ?? [];

  const [myScoreCardItem, setMyScoreCardItem] =
    React.useState<ScoreCard>();

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerData, setDrawerData] = React.useState<DrawerCore>();
  const [parDrawerOpen, setParDrawerOpen] = React.useState(false);
  const [parDrawerData, setParDrawerData] = React.useState<DrawerCore>();
  const [currentHoleNumber, setCurrentHoleNumber] = React.useState<number>(1);
  const pendingFocusHoleRef = React.useRef<number | null>(null);

  const [activeTab, setActiveTab] = React.useState<"scorecard" | "ranking">(
    "ranking",
  );

  // 저장 성공 전까지 홀별 입력값 임시 보관
  const [pendingPars, setPendingPars] = React.useState<Record<number, number>>(
    {},
  );
  const [pendingScores, setPendingScores] = React.useState<
    Record<number, number>
  >({});

  // 재조회 후 임시값 정리
  const putScoreMutation = useMutation({
    mutationFn: putScoreInput,
    onSuccess: async (res) => {
      if (!res.success) return;
      await getQueryClient().invalidateQueries({
        queryKey: scoreDetailQuery.queryKey,
      });
    },
    onError: (err) => {
      pendingFocusHoleRef.current = null;
      alertRequestFailure("스코어 저장 실패", err);
    },
  });

  const deleteScoreCardMutation = useMutation({
    mutationFn: (scoreId: string | number) => scoreCardDelete(scoreId),
    onSuccess: (res) => {
      if (!res.success) return;
      getQueryClient().invalidateQueries({ queryKey: ["score-list"] });
      navigate("/score");
    },
    onError: (err) => alertRequestFailure("스코어카드 삭제 실패", err),
  });

  // 저장 중 새로 입력한 값은 남긴다
  const clearSaved = (
    holeNo: number,
    saved: { par?: number; score: number },
  ) => {
    const withoutIf =
      (matches: (value: number | undefined) => boolean) =>
      (prev: Record<number, number>) => {
        if (!matches(prev[holeNo])) return prev;
        const next = { ...prev };
        delete next[holeNo];
        return next;
      };
    setPendingPars(
      withoutIf((v) => saved.par === undefined || v === saved.par),
    );
    setPendingScores(withoutIf((v) => v === saved.score));
  };

  /** par/score 입력을 보관하고, 저장에 필요한 값이 갖춰지면 update-score 호출 */
  const commitHole = (
    item: ScoreCard,
    holeNo: number,
    { par, score }: { par?: number; score?: number },
  ) => {
    const hole = holeAt(item, holeNo);
    if (!item.scoreCardId || hole?.holeScoreId == null) return;

    if (par !== undefined)
      setPendingPars((prev) => ({ ...prev, [holeNo]: par }));
    if (score !== undefined)
      setPendingScores((prev) => ({ ...prev, [holeNo]: score }));
    setParDrawerOpen(false);
    setDrawerOpen(false);

    const parRequired = needsUserPar(item, holeNo);
    const userPar = parRequired
      ? (par ?? pendingPars[holeNo] ?? hole.par ?? undefined)
      : undefined;
    const resolvedScore =
      score ?? pendingScores[holeNo] ?? hole.score ?? undefined;
    // par/score 한쪽만 입력된 경우: 보관만 하고 포커스는 이동하지 않음
    if (resolvedScore === undefined) return;
    if (parRequired && userPar === undefined) return;

    // par 입력 시에도 스코어처럼 다음 홀(다음 파)로 포커스를 이동
    if (par !== undefined)
      pendingFocusHoleRef.current = inputHoleNoAfter(item, holeNo);
    // 연속 저장에도 호출별 결과를 받는다
    putScoreMutation
      .mutateAsync({
        scoreCardId: item.scoreCardId,
        holeScoreId: hole.holeScoreId,
        score: resolvedScore,
        putts: 0,
        ...(userPar === undefined ? {} : { par: userPar }),
      })
      .then((res) => {
        if (res.success)
          clearSaved(holeNo, { par: userPar, score: resolvedScore });
      })
      // 실패 안내는 onError 가 한다
      .catch(() => {});
  };

  const buildDrawerCore = (
    item: ScoreCard,
    holeNo: number,
  ): DrawerCore | null => {
    const hole = holeAt(item, holeNo);
    if (!item.scoreCardId || hole?.holeScoreId == null) return null;

    const serverPar = hole.par;
    const rawPar = pendingPars[holeNo] ?? serverPar;
    const safePar =
      typeof rawPar === "number" && !Number.isNaN(rawPar) ? rawPar : 3;

    const resolvedScore = pendingScores[holeNo] ?? hole.score;

    return {
      holeNo,
      par: safePar,
      isEdit: resolvedScore != null || !!myScoreCardItem?.isComplete,
      score: resolvedScore,
    };
  };

  /** 드로어 열기: 입력할 수 있는 홀일 때만 현재 홀로 세팅 */
  const openDrawer = (item: ScoreCard, holeNo = currentHoleNumber) => {
    const core = buildDrawerCore(item, holeNo);
    if (!core) return;
    setCurrentHoleNumber(holeNo);
    setDrawerData(core);
    setDrawerOpen(true);
  };

  const openParDrawer = (item: ScoreCard, holeNo = currentHoleNumber) => {
    const core = buildDrawerCore(item, holeNo);
    if (!core) return;
    setCurrentHoleNumber(holeNo);
    setParDrawerData(core);
    setParDrawerOpen(true);
  };

  React.useEffect(() => {
    if (isSuccess && scoreCardList.length > 0) {
      const holeNumber =
        pendingFocusHoleRef.current ?? resumeHoleNo(scoreCardList[0]);
      pendingFocusHoleRef.current = null;
      setCurrentHoleNumber(holeNumber);

      setMyScoreCardItem(scoreCardList[0]);
    }
  }, [isSuccess, scoreCardList]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <AppBar
        backButton
        title="스코어 확인"
        onBack={() => navigate(-1)}
        right={
          <div className="flex shrink-0 gap-x-[6px]">
            {tournamentName && (
              <button
                onClick={handleRefresh}
                className="p-1 flex items-center justify-center"
                aria-label="새로고침"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={isSpinning ? "animate-spin" : ""}
                >
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
              </button>
            )}
            {!tournamentName && (
              <button
                onClick={() => {
                  if (!id) return;
                  if (deleteScoreCardMutation.isPending) return;
                  openModal("CONFIRM_DIALOG", {
                    title: "스코어카드 삭제",
                    content:
                      "스코어카드를 삭제하시겠습니까?\n삭제 후에는 복구할 수 없습니다.",
                    onClick: () => deleteScoreCardMutation.mutate(id),
                  });
                }}
                aria-label="스코어카드 삭제"
                className="p-1 flex items-center justify-center"
              >
                <img className="size-[20px]" src="/icons/ic-delete.webp" />
              </button>
            )}
          </div>
        }
      />

      <section className="px-[15px] min-h-screen min-w-0 w-full space-y-4">
        {/* {isMultiRound && roundList.length > 0 && (
          <div className="pt-[10px]">
            <SelectBox
              options={roundList.map((r) => ({
                label: r.roundName,
                value: String(r.id),
              }))}
              value={selectedRoundId != null ? String(selectedRoundId) : ""}
              onChange={(e) => setSelectedRoundId(Number(e.target.value))}
            />
          </div>
        )} */}
        {/* 상단 카드 */}
        <div className="flex flex-row gap-x-[20px] bg-[#f5f5f5] px-[20px] py-[14px] rounded-[15px] items-start min-w-0">
          {/* 왼쪽 포스터 */}
          <img
            style={{ width: vw(83), height: vw(tournamentId ? 117 : 83) }}
            className={`rounded-[10px] object-cover mt-[6px] shrink-0 ${thumbnail ? "" : "bg-white"}`}
            src={
              thumbnail
                ? staticURL(thumbnail)
                : myScoreCardItem?.golfParkImageUrl
                  ? staticURL(myScoreCardItem.golfParkImageUrl)
                  : "/images/img-login-logo.webp"
            }
            alt="poster"
          />

          {/* 오른쪽 텍스트 */}
          <div
            className={`flex min-w-0 flex-1 flex-col ${tournamentId ? "justify-between gap-y-[6px] py-[5px]" : "justify-center gap-[3px] py-0"}`}
          >
            <p className="font-bold text-[#222] text-2xl leading-snug break-keep [overflow-wrap:anywhere]">
              {tournamentName || myScoreCardItem?.golfParkName}
            </p>

            <div
              className={`${tournamentId ? "mt-[8px]" : ""} flex gap-[3px] text-[#888] font-[500] min-w-0`}
            >
              <img
                className="size-3.5 mt-[4px] shrink-0"
                src="/icons/ic-calendarcheck.webp"
                alt="calendar"
              />
              <span className="text-sm break-keep">
                {formatKoreanDate(myScoreCardItem?.roundDate || "")}
              </span>
            </div>
            <p className="text-sm mt-1 break-keep [overflow-wrap:anywhere]">
              {myScoreCardItem?.roundName}
            </p>

            {tournamentId && (
              <div className="mt-[8px] flex gap-[10px] text-[#222] font-bold min-w-0">
                <img
                  className="size-4 mt-1 shrink-0"
                  src="/icons/ic-tournament-team.webp"
                  alt="trophy"
                />
                <span className="min-w-0 break-keep [overflow-wrap:anywhere]">
                  {teamName}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 탭: 대회(tournamentName)일 때만 노출 */}
        {tournamentName && (
          <AnimatedCoverTabs
            tabs={[
              { label: "순위", value: "ranking" },
              { label: "스코어카드", value: "scorecard" },
            ]}
            value={activeTab}
            onChange={(value) => setActiveTab(value as "scorecard" | "ranking")}
            className="mt-[15px]"
          />
        )}

        {tournamentName && activeTab === "ranking" && (
          <RankingTab
            tournamentId={tournamentId}
            tournamentType={tournamentType}
            defaultCalendarId={scoreCardList[0]?.calendarId}
          />
        )}

        {/* 스코어카드: 일반 스코어이거나(탭 없음) 스코어카드 탭 선택 시 */}
        {(!tournamentName || activeTab === "scorecard") && (
          <ScorecardTab
            myScoreCardItem={myScoreCardItem}
            scoreCardList={scoreCardList}
            currentOvertimes={currentOvertimes}
            currentUserId={currentUserId}
            pendingPars={pendingPars}
            pendingScores={pendingScores}
            tournamentId={tournamentId}
            currentHoleNumber={currentHoleNumber}
            onOpenDrawer={openDrawer}
            onOpenParDrawer={openParDrawer}
          />
        )}
      </section>

      {/* 입력 완료 버튼 */}
      {/* {!submitted && (
        <div className={cn("bg-white p-[15px] fixed max-w-[440px] w-full bottom-0 flex-row-between gap-[10px] z-50")}>
          <Button
            type="button"
            className="w-full font-bold h-[50px]"
            variant="red"
            label="스코어 입력 완료"
            onClick={() => {
              setIsSubmitted(true);   // 제출 상태로 전환
              setIsEdited(false);     // 직전까지의 수정 플래그 리셋!
            }}
          />
        </div>
      )} */}

      {/* 수정 완료 버튼: 제출된 상태(서버 complete 포함)에서, 이번 세션에 수정 발생했을 때만 */}
      {/* {submitted && isEdited && (
        <div className={cn("bg-white p-[15px] fixed max-w-[440px] w-full bottom-0 flex-row-between gap-[10px] z-50")}>
          <Button
            type="button"
            className="w-full font-bold h-[50px]"
            variant="black"
            label="스코어 수정 완료"
            onClick={() => setIsEdited(false)}
          />
        </div>
      )}
      */}

      {drawerOpen && myScoreCardItem && drawerData && (
        <ScorePersonalDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          data={drawerData}
          courseName={courseLabelOf(
            myScoreCardItem,
            courseOrderOf(drawerData.holeNo),
          )}
          onSubmit={(score) =>
            commitHole(myScoreCardItem, drawerData.holeNo, { score })
          }
          onMoveHole={(step) => {
            const next = nextInputHoleNo(
              myScoreCardItem,
              drawerData.holeNo,
              step,
            );
            if (next != null) openDrawer(myScoreCardItem, next);
          }}
        />
      )}

      {parDrawerOpen && myScoreCardItem && parDrawerData && (
        <ParPersonalDrawer
          open={parDrawerOpen}
          onClose={() => setParDrawerOpen(false)}
          data={parDrawerData}
          courseName={courseLabelOf(
            myScoreCardItem,
            courseOrderOf(parDrawerData.holeNo),
          )}
          onSubmit={(par) => {
            commitHole(myScoreCardItem, parDrawerData.holeNo, { par });
          }}
        />
      )}
    </div>
  );
}
